import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { lobbyAttendeeApi } from '../lib/api'
import { useGrantRedeem } from './useGrantRedeem'
import type { AskResponse } from '../types/lobbyDay'

export interface RecordOutcomeInput {
  meetingId: string
  response: AskResponse
  notes?: string
}

/**
 * L11 Slice 2 (Task 5): the attendee-side credential seam. Bridges the anonymous packet/schedule
 * token (`token`, read-only, resolved by the legacy `lobbyAttendeeApi.getSchedule/getPacket`
 * path) to the grant-cookie-authed write path added in Slice 1a/Task 3: a WRITE (recording an
 * outcome) first redeems the token into a `grant_session` cookie, then calls the outcome
 * endpoint.
 *
 * Redeem has three outcomes:
 *  - success -> cookie set, proceed straight to recordOutcome.
 *  - `{ termsRequired: true }` -> no cookie yet; surface `termsRequired` so the sheet shows the
 *    [LEGAL] acknowledgement step, then `acceptTermsAndRecord` re-redeems with
 *    `acceptTerms: true` + the `termsVersion` the server told us to echo back.
 *  - 404 -> a pre-reseed attendee link is valid for reads but has no paired grant yet. Caught
 *    inside `useGrantRedeem` (never thrown to this hook's caller) and surfaced as `unavailable`
 *    so the sheet can show a "check with your organizer" message instead of crashing.
 */
export function useAttendeeSession(token: string | undefined, onRecorded?: () => void) {
  const { ensureSession, termsRequired, unavailable } = useGrantRedeem(token)

  const mutation = useMutation({
    mutationFn: async (input: RecordOutcomeInput & { acceptTerms?: boolean }) => {
      const outcome = await ensureSession(input.acceptTerms)
      if (outcome !== 'ok') {
        // `ensureSession` already set `termsRequired`/`unavailable` state (and, for a redeem
        // 404, swallowed the error -- never throws here). Nothing left to record.
        return { termsRequired: outcome === 'termsRequired' } as const
      }

      const receipt = await lobbyAttendeeApi.recordOutcome({
        meetingId: input.meetingId,
        response: input.response,
        notes: input.notes,
      })
      return { termsRequired: false as const, receipt }
    },
  })

  // A real save (as opposed to a terms bounce or a 404) is the ONLY branch that returns a
  // `receipt` -- fire `onRecorded` only then, so the host can close the sheet + flip its label.
  const onMutateSuccess = (data: { termsRequired: boolean; receipt?: unknown }) => {
    if ('receipt' in data && data.receipt) onRecorded?.()
  }

  // Depend on `.mutate` (stable across renders), never the whole `mutation` object -- TanStack
  // returns a new object every render, and depending on it here caused an app-freezing render
  // loop before (see [[usemutation-unstable-object-deps]]).
  const recordOutcome = useCallback(
    (input: RecordOutcomeInput) => mutation.mutate(input, { onSuccess: onMutateSuccess }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation.mutate, onRecorded],
  )

  const acceptTermsAndRecord = useCallback(
    (input: RecordOutcomeInput) => mutation.mutate({ ...input, acceptTerms: true }, { onSuccess: onMutateSuccess }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation.mutate, onRecorded],
  )

  return {
    recordOutcome,
    termsRequired,
    acceptTermsAndRecord,
    isRecording: mutation.isPending,
    unavailable,
  }
}
